/**
 * Internal Job Schedule Drift Contract (SCH-2329)
 *
 * `GET /internal/scheduled-tasks/drift`: where production's `ScheduledTask` rows and execution
 * history disagree with what the backend's Job Catalog (`apps/backend/src/lib/queue/job-catalog.ts`)
 * intends. Read-only, internal API key, read replica. The backend decides every finding; the
 * `pnpm sj job-schedules` verb only maps the three finding lists to an exit verdict.
 *
 * Lane and trigger travel as plain strings: the closed unions live in the catalog, which this
 * package does not import, and a new lane must not break an older CLI's parse.
 */

import { z } from 'zod'

/** The default look-back for "no execution". Execution rows are retained for less (see `evidenceSince`). */
export const JOB_SCHEDULE_DRIFT_DEFAULT_WINDOW_DAYS = 30

export const JOB_SCHEDULE_DRIFT_MAX_WINDOW_DAYS = 90

export const InternalJobScheduleDriftRequestSchema = z.object({
  window_days: z.coerce
    .number()
    .int()
    .min(1)
    .max(JOB_SCHEDULE_DRIFT_MAX_WINDOW_DAYS)
    .default(JOB_SCHEDULE_DRIFT_DEFAULT_WINDOW_DAYS)
})

export type InternalJobScheduleDriftRequest = z.infer<typeof InternalJobScheduleDriftRequestSchema>

/** Kind 1: the catalog declares the type `scheduled`, but no ENABLED row fires it. */
export const DeclaredScheduledNoRowFindingSchema = z.object({
  jobType: z.string(),
  lane: z.string(),
  /** Disabled rows that target this type. Non-empty means the schedule exists but is switched off. */
  disabledTaskNames: z.array(z.string())
})

export type DeclaredScheduledNoRowFinding = z.infer<typeof DeclaredScheduledNoRowFindingSchema>

/**
 * Kind 2: an ENABLED row that fires `/internal/jobs/trigger` for a type the catalog does not
 * declare `scheduled`.
 * - `not-declared-scheduled`: a catalog type whose trigger intent is `enqueued` or `manual`.
 * - `unknown-job-type`: `jobType` is not in the catalog; the trigger route 400s every fire.
 * - `missing-job-type`: the body carries no string `jobType`; the trigger route 400s every fire.
 */
export const RowNotDeclaredScheduledFindingSchema = z.object({
  taskName: z.string(),
  jobType: z.string().nullable(),
  /** The catalog's trigger intent, or null when the type is not in the catalog. */
  trigger: z.string().nullable(),
  reason: z.enum(['not-declared-scheduled', 'unknown-job-type', 'missing-job-type'])
})

export type RowNotDeclaredScheduledFinding = z.infer<typeof RowNotDeclaredScheduledFindingSchema>

/** Kind 3: a type with an ENABLED row and no attributed execution since `windowStart`. */
export const NoRecentExecutionFindingSchema = z.object({
  jobType: z.string(),
  lane: z.string(),
  /** The enabled rows that should have fired it. */
  taskNames: z.array(z.string()),
  /** The newest attributed execution still retained, if any (older than `windowStart`). */
  lastExecutionAt: z.string().nullable()
})

export type NoRecentExecutionFinding = z.infer<typeof NoRecentExecutionFindingSchema>

export const InternalJobScheduleDriftResponseSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  windowStart: z.string(),
  /**
   * The oldest execution row still in the table. The nightly execution cleanup keeps about 15
   * days, so when this is later than `windowStart` the real evidence window is shorter than
   * `windowDays`. Null when the table is empty.
   */
  oldestRetainedExecutionAt: z.string().nullable(),
  counts: z.object({
    catalogJobTypes: z.number(),
    declaredScheduled: z.number(),
    rows: z.number(),
    enabledJobRows: z.number(),
    disabledRows: z.number(),
    otherEndpointRows: z.number(),
    /** Types with an enabled row: the population checked for kind 3. */
    checkedForExecutions: z.number()
  }),
  findings: z.object({
    declaredScheduledNoRow: z.array(DeclaredScheduledNoRowFindingSchema),
    rowNotDeclaredScheduled: z.array(RowNotDeclaredScheduledFindingSchema),
    noRecentExecution: z.array(NoRecentExecutionFindingSchema)
  }),
  /** Rows that target another endpoint (not a background job). Listed, never a finding. */
  otherEndpointRows: z.array(z.object({ taskName: z.string(), endpointPath: z.string(), enabled: z.boolean() })),
  /** Execution names in the window that map to no job type. Informational. */
  unattributedExecutionNames: z.array(
    z.object({ taskName: z.string(), executions: z.number(), lastExecutionAt: z.string() })
  )
})

export type InternalJobScheduleDriftResponse = z.infer<typeof InternalJobScheduleDriftResponseSchema>
